import React, { useState, useCallback, useEffect } from 'react';
import { DateBox as DXDateBox } from 'devextreme-react/date-box';
import { Validator, RequiredRule } from 'devextreme-react/validator';
import type { OptionChangedEvent, ValueChangedEvent } from 'devextreme/ui/date_box';
import { ValidatedEvent } from 'devextreme/ui/validator';
import { formatDateLocal, formatToSqlDateTime, isDate, validateDateString } from '@apply/ui/src/lib/helpers';
import '../../../scss/controls/_datetimebox.scss';
import { InfoIconButton } from '../InfoIconButton';

export enum DateTimeBoxLabelSize {
    None,
    Small,
    SmallBold,
    Large,
}

interface DateTimeBoxProps {
    id?: string;
    dataTestid?: string;
    value: string | number | Date | undefined;
    readOnly?: boolean;
    label?: string;
    width?: string;
    placeholder?: string;
    includeTime?: boolean;
    onChange: (value: string) => void;
    required?: boolean;
    labelSize?: DateTimeBoxLabelSize;
    onValidationEvent?: (isValid: boolean) => void;
    showInfoIcon?: boolean;
    infoText?: string;
    max?: Date;
    dateOutOfRangeMessage?: string;
}

export const DateTimeBox: React.FC<DateTimeBoxProps> = ({
    id,
    dataTestid,
    value,
    readOnly = false,
    label,
    width = 'auto',
    placeholder,
    includeTime = false,
    onChange,
    required,
    labelSize = DateTimeBoxLabelSize.None,
    onValidationEvent,
    showInfoIcon = false,
    infoText,
    max,
    dateOutOfRangeMessage = 'Date is out of range',
}) => {
    const [internalDate, setInternalDate] = useState<Date | undefined>();
    const [error, setError] = useState<string>('');

    const formatErrorMessage = 'Please enter a date in MM/DD/YYYY format';

    // DevExtreme runs its own min/max and parse checks before firing onValueChanged. When one of
    // those fails it sets validationError on the editor and never raises onValueChanged, so mirror
    // that editor-specific message into the wrapper's error line.
    const handleOptionChanged = (e: OptionChangedEvent) => {
        if (e.name !== 'validationError') {
            return;
        }
        const validationError = e.value as { editorSpecific?: boolean; message?: string } | null | undefined;
        if (validationError?.editorSpecific && validationError.message) {
            setError(validationError.message);
        } else if (!validationError) {
            setError('');
        }
    };

    const dataAndErrorHandler = (errorMsg: string, e?: ValueChangedEvent) => {
        e?.component.option('value', undefined);
        setError(errorMsg);
    };

    useEffect(() => {
        let date: Date | undefined;
        if (value) {
            const parsed = new Date(value as string | number | Date);
            if (!isNaN(parsed.getTime())) {
                date = parsed;
            }
        }
        setInternalDate(date);
    }, [value, includeTime]);

    const handleValueChanged = useCallback(
        (e: ValueChangedEvent) => {
            const enteredValue = e.value;
            // Empty/null/undefined — let the Validator handle required validation
            if (enteredValue === undefined || enteredValue === '' || enteredValue === null) {
                return;
            }
            // Handle non-four-digit years
            if (
                typeof enteredValue === 'string' &&
                enteredValue.split('/')[0].startsWith('0') &&
                enteredValue.split('/')[0]?.length === 4
            ) {
                dataAndErrorHandler(formatErrorMessage, e);
                return;
            }
            // Check for a Date object (this can occur from both the datepicker and typed text)
            if (isDate(enteredValue)) {
                const formattedDate = formatDateLocal(enteredValue);
                if (!validateDateString(formattedDate)) {
                    dataAndErrorHandler(formatErrorMessage, e);
                    return;
                }
                const newDate = new Date(enteredValue);
                if (!includeTime) {
                    newDate.setHours(0, 0, 0, 0);
                }
                setInternalDate(newDate);
                onChange(formatToSqlDateTime(newDate));
                setError('');
            } else {
                // Run this else block if the date shows up as a text value
                const reformatChangedDate = formatDateLocal(new Date(enteredValue));
                if (!validateDateString(reformatChangedDate)) {
                    dataAndErrorHandler(formatErrorMessage, e);
                    return;
                }
                // Re-save the value if already-selected calendar entries are modified but are still valid
                const newDate = new Date(reformatChangedDate);
                if (!includeTime) {
                    newDate.setHours(0, 0, 0, 0);
                }
                onChange(formatToSqlDateTime(newDate));
                setInternalDate(newDate);
                setError('');
            }
        },
        [includeTime, onChange]
    );

    const onValidated = (validatedInfo: ValidatedEvent) => {
        setError(validatedInfo?.brokenRule?.message ?? '');
        if (onValidationEvent) {
            onValidationEvent(validatedInfo.isValid ?? false);
        }
    };

    const getLabelClassName = () => {
        switch (labelSize) {
            case DateTimeBoxLabelSize.Large:
                return ' large';
            case DateTimeBoxLabelSize.SmallBold:
                return ' smallBold';
            default:
                return '';
        }
    };

    const getControlSpacingTop = () => {
        switch (labelSize) {
            case DateTimeBoxLabelSize.Large:
                return '22px';
            default:
                return;
        }
    };

    const getControlSpacingBottom = () => {
        switch (labelSize) {
            case DateTimeBoxLabelSize.Large:
                return '20px';
            default:
                return;
        }
    };

    return (
        <div
            id={id}
            data-testid={dataTestid}
            className='datetimebox-container standardSpacing'
        >
            {label && (
                <div>
                    <label
                        className={`datetimebox-label${getLabelClassName()}${error ? ' error' : ''}`}
                        style={{ marginTop: getControlSpacingTop(), marginBottom: getControlSpacingBottom() }}
                    >
                        {required ? label + ' *' : label}
                    </label>
                    {showInfoIcon && <InfoIconButton modalText={infoText} />}
                </div>
            )}

            <div
                className={`datetimebox-input${error ? ' error' : ''}`}
                style={{ width: width }}
            >
                <DXDateBox
                    stylingMode='outlined'
                    placeholder={placeholder}
                    value={internalDate ?? null}
                    readOnly={readOnly}
                    acceptCustomValue={true}
                    onValueChanged={handleValueChanged}
                    onOptionChanged={handleOptionChanged}
                    elementAttr={{ class: 'dx-texteditor dx-texteditor-outlined' }}
                    inputAttr={{ class: 'dx-texteditor-input' }}
                    invalidDateMessage={formatErrorMessage}
                    width={width}
                    type={includeTime ? 'datetime' : 'date'}
                    displayFormat={includeTime ? 'MM/dd/yyyy HH:mm' : 'MM/dd/yyyy'}
                    max={max}
                    dateOutOfRangeMessage={dateOutOfRangeMessage}
                >
                    <Validator onValidated={onValidated}>
                        {required && <RequiredRule message='This field is required' />}
                    </Validator>
                </DXDateBox>
            </div>
            {error && <div className='datetimebox-error'>{error}</div>}
        </div>
    );
};
